import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('fm_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      // Auto-signout on token expiry
      const path = window.location.pathname;
      if (!path.startsWith('/login')) {
        localStorage.removeItem('fm_token');
        localStorage.removeItem('fm_user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

export const authApi = {
  requestOtp: (email) => api.post('/auth/request-otp', { email }).then(r => r.data),
  verifyOtp: (email, otp) => api.post('/auth/verify-otp', { email, otp }).then(r => r.data),
  me: () => api.get('/auth/me').then(r => r.data),
};

export const usersApi = {
  list: () => api.get('/users').then(r => r.data),
  add: (u) => api.post('/users', u).then(r => r.data),
  bulk: (rows) => api.post('/users/bulk', { rows }).then(r => r.data),
  update: (id, patch) => api.put(`/users/${id}`, patch).then(r => r.data),
  remove: (id) => api.delete(`/users/${id}`).then(r => r.data),
  sendInvite: (id) => api.post(`/users/${id}/send-invite`).then(r => r.data),
  listDeleted: () => api.get('/users/deleted').then(r => r.data),
  restore: (email) => api.post('/users/deleted/restore', { email }).then(r => r.data),
};

export const ebosApi = {
  list: () => api.get('/ebos').then(r => r.data),
  add: (e) => api.post('/ebos', e).then(r => r.data),
  update: (code, patch) => api.put(`/ebos/${code}`, patch).then(r => r.data),
  sendWelcome: (code) => api.post(`/ebos/${encodeURIComponent(code)}/send-welcome`).then(r => r.data),
  remove: (code) => api.delete(`/ebos/${code}`).then(r => r.data),
  bulkDelete: (codes) => api.post('/ebos/bulk-delete', { codes }).then(r => r.data),
  bulkAssignBrand: (codes, brand) => api.post('/ebos/bulk-assign-brand', { codes, brand }).then(r => r.data),
  templateUrl: () => `${API_BASE}/ebos/template`,
  exportUrl: () => `${API_BASE}/ebos/export`,
  import: (file, onUploadProgress) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/ebos/import', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    }).then(r => r.data);
  },
};

export const skusApi = {
  list: () => api.get('/skus').then(r => r.data),
  add: (s) => api.post('/skus', s).then(r => r.data),
  update: (sku, patch) => api.put(`/skus/${sku}`, patch).then(r => r.data),
  remove: (sku) => api.delete(`/skus/${sku}`).then(r => r.data),
  bulkDelete: (skus) => api.post('/skus/bulk-delete', { skus }).then(r => r.data),
  bulkAssignBrand: (skus, brand) => api.post('/skus/bulk-assign-brand', { skus, brand }).then(r => r.data),
  templateUrl: () => `${API_BASE}/skus/template`,
  exportUrl: () => `${API_BASE}/skus/export`,
  import: (file, onUploadProgress) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/skus/import', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    }).then(r => r.data);
  },
};

export const salesApi = {
  list: () => api.get('/sales').then(r => r.data),
  add: (s) => api.post('/sales', s).then(r => r.data),
  bulkDelete: ({ ids, filter }) => api.post('/sales/bulk-delete', { ids, filter }).then(r => r.data),
  templateUrl: () => `${API_BASE}/sales/template`,
  exportUrl: () => `${API_BASE}/sales/export`,
  import: (file, onUploadProgress) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/sales/import', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    }).then(r => r.data);
  },
  syncStatus: () => api.get('/sales/sync-status').then(r => r.data),
  syncNow: () => api.post('/sales/sync-now').then(r => r.data),
  importRuns: () => api.get('/sales/import-runs').then(r => r.data),
  importRunsExportUrl: (from, to) => `${API_BASE}/sales/import-runs/export?from=${from}&to=${to}`,
  erpDigest: () => api.get('/sales/erp-digest').then(r => r.data),
  saveErpDigest: (recipients) => api.put('/sales/erp-digest', { recipients }).then(r => r.data),
  sendErpDigestNow: () => api.post('/sales/erp-digest/send-now').then(r => r.data),
};

export const validationApi = {
  attempts: (params) => api.get('/validation/attempts', { params }).then(r => r.data),
  exportUrl: (params) => `${API_BASE}/validation/attempts/export?${new URLSearchParams(Object.entries(params).filter(([, v]) => v))}`,
  coolingHistory: (sku) => api.get(`/skus/${encodeURIComponent(sku)}/cooling-history`).then(r => r.data),
  trends: (weeks) => api.get('/validation/trends', { params: { weeks } }).then(r => r.data),
};

export const stockApi = {
  list: (params) => api.get('/stock', { params }).then(r => r.data),
  mine: () => api.get('/stock/mine').then(r => r.data),
  config: () => api.get('/stock/config').then(r => r.data),
  saveConfig: (hoBranchCode) => api.put('/stock/config', { hoBranchCode }).then(r => r.data),
  import: (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/stock/import', fd).then(r => r.data); },
  syncNow: () => api.post('/stock/sync-now').then(r => r.data),
  reports: () => api.get('/stock/import-reports').then(r => r.data),
  reportUrl: (id) => `${API_BASE}/stock/import-reports/${id}/download`,
  templateUrl: `${API_BASE}/stock/template`,
  exportUrl: `${API_BASE}/stock/export`,
};

export const requisitionsApi = {
  list: (params = {}) => api.get('/requisitions', { params }).then(r => r.data),
  get: (id) => api.get(`/requisitions/${encodeURIComponent(id)}`).then(r => r.data),
  create: (payload, idempotencyKey) => {
    // Backwards-compat: caller may pass either the raw lines array (old
    // callers) OR a full body object {lines, backdate?, eboBranchCode?}.
    const body = Array.isArray(payload) ? { lines: payload } : payload;
    const headers = idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {};
    return api.post('/requisitions', body, { headers }).then(r => r.data);
  },
  updateLine: (id, idx, patch) => api.put(`/requisitions/${encodeURIComponent(id)}/lines/${idx}`, patch).then(r => r.data),
  bulkLineUpdate: (id, body) => api.post(`/requisitions/${encodeURIComponent(id)}/lines/bulk`, body).then(r => r.data),
  update: (id, patch) => api.put(`/requisitions/${encodeURIComponent(id)}`, patch).then(r => r.data),
  remove: (id) => api.delete(`/requisitions/${encodeURIComponent(id)}`).then(r => r.data),
  cooldown: (branch) => api.get('/requisitions/cooldown', { params: branch ? { branch } : {} }).then(r => r.data),
  validate: (payload) => api.post('/requisitions/validate', payload).then(r => r.data),
  bulkHistorical: (rows, opts = {}) => api.post('/requisitions/bulk-historical', { rows, ...opts }).then(r => r.data),
  bulkHistoricalDryRun: (rows) => api.post('/requisitions/bulk-historical', { rows, dryRun: true }).then(r => r.data),
  bulkHistoricalUndo: (batchId) => api.post(`/requisitions/bulk-historical/${encodeURIComponent(batchId)}/undo`).then(r => r.data),
  softDeleteHistorical: (id, reason) => api.post(`/requisitions/${encodeURIComponent(id)}/soft-delete`, { reason }).then(r => r.data),
  restoreDeleted: (id) => api.post(`/requisitions/${encodeURIComponent(id)}/restore`).then(r => r.data),
  listDeletedHistorical: () => api.get('/requisitions/deleted-historical').then(r => r.data),
  restoreBulk: (reqIds) => api.post('/requisitions/restore-bulk', { reqIds }).then(r => r.data),
  hardDeleteBulk: (reqIds) => api.post('/requisitions/hard-delete-bulk', { reqIds }).then(r => r.data),
  addNote: (id, text) => api.post(`/requisitions/${encodeURIComponent(id)}/notes`, { text }).then(r => r.data),
  exportBulk: (reqIds) => api.post('/requisitions/export-bulk', { reqIds }, { responseType: 'blob' }).then(r => r.data),
  // One-click "mark this requisition" — stamps status + shared courier + tracking
  // on every line. Used by Warehouse/Logistics via the ShipMarkModal.
  markAll: (id, body) => api.post(`/requisitions/${encodeURIComponent(id)}/mark`, body).then(r => r.data),
  // Fetch the courier whitelist + tracking-number rules from the backend so
  // the UI never falls out of sync with server-side validation.
  couriers: () => api.get('/requisitions/config/couriers').then(r => r.data),
  bulkHistoricalParseFile: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/requisitions/bulk-historical/parse-file', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  // iter37 — Warehouse's per-line accept/reject/hold verdict on a Requested requisition.
  warehouseReview: (id, body) => api.post(`/requisitions/${encodeURIComponent(id)}/warehouse-review`, body).then(r => r.data),
  exportUrl: (id) => `${API_BASE}/requisitions/${encodeURIComponent(id)}/export`,
  waybillUrl: (id) => `${API_BASE}/requisitions/${encodeURIComponent(id)}/waybill`,
  waybillsBulkUrl: () => `${API_BASE}/requisitions/waybills/bulk`,
};

export const settingsApi = {
  getTheme: () => api.get('/theme').then(r => r.data),
  setTheme: (t) => api.put('/theme', t).then(r => r.data),
  getAutomailer: () => api.get('/automailer').then(r => r.data),
  setAutomailer: (a) => api.put('/automailer', a).then(r => r.data),
  previewAutomailer: (payload) => api.post('/automailer/preview', payload).then(r => r.data),
  testSendAutomailer: (payload) => api.post('/automailer/test-send', payload).then(r => r.data),
  mailLog: () => api.get('/mail-log').then(r => r.data),
};

export const rbacApi = {
  permissions: () => api.get('/rbac/permissions').then(r => r.data),
  getMatrix: () => api.get('/rbac/matrix').then(r => r.data),
  setMatrix: (matrix) => api.put('/rbac/matrix', { matrix }).then(r => r.data),
};

export const reportsApi = {
  overview: () => api.get('/reports/overview').then(r => r.data),
  testerToSales: (ebo = 'all', range = '6') => api.get('/reports/tester-to-sales', { params: { ebo, range } }).then(r => r.data),
  testerToSalesExportUrl: (ebo = 'all', range = '6') => `${API_BASE}/reports/tester-to-sales/export?ebo=${encodeURIComponent(ebo)}&range=${encodeURIComponent(range)}`,
  testerCoverage: () => api.get('/reports/tester-coverage').then(r => r.data),
  testerCoverageExportUrl: () => `${API_BASE}/reports/tester-coverage/export`,
  pendingRequisitionsExportUrl: () => `${API_BASE}/reports/pending-requisitions/export`,
  digestConfig: () => api.get('/reports/fulfilment-digest/config').then(r => r.data),
  setDigestConfig: (cfg) => api.put('/reports/fulfilment-digest/config', cfg).then(r => r.data),
  digestPreview: () => api.get('/reports/fulfilment-digest/preview').then(r => r.data),
  sendDigest: () => api.post('/reports/fulfilment-digest/send').then(r => r.data),
};

export const filterTemplatesApi = {
  list: (scope) => api.get('/filter-templates', { params: { scope } }).then(r => r.data),
  save: (payload) => api.post('/filter-templates', payload).then(r => r.data),
  remove: (id) => api.delete(`/filter-templates/${encodeURIComponent(id)}`).then(r => r.data),
  toggleShare: (id, shared) => api.put(`/filter-templates/${encodeURIComponent(id)}/share`, { shared }).then(r => r.data),
};

export const attachmentsApi = {
  upload: (file, onProgress) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/attachments', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    }).then(r => r.data);
  },
  // Mint a fresh short-lived signed URL for HTML thumbnails. The URL is
  // valid for ~5 minutes and carries an HMAC — NEVER the caller's JWT.
  sign: (id) => api.post(`/attachments/${encodeURIComponent(id)}/sign`).then(r => r.data),
  // Build an absolute URL from a signed relative path returned by the
  // backend (`/api/attachments/{id}?sig=…&exp=…`). Returns undefined for
  // empty inputs so `<img src={…}>` stays quiet on missing data.
  absoluteUrl: (signedPath) => (signedPath ? `${API_BASE}${signedPath.replace(/^\/api/, '')}` : undefined),
};

export const adequacyApi = {
  mine: (days = 30) => api.get('/reports/attachment-adequacy/mine', { params: { days } }).then(r => r.data),
  byBranch: (days = 30) => api.get('/reports/attachment-adequacy/by-branch', { params: { days } }).then(r => r.data),
};

export const warehouseApi = {
  get: () => api.get('/warehouse-address').then(r => r.data),
  update: (body) => api.put('/warehouse-address', body).then(r => r.data),
  uploadLogo: (file, onProgress) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/warehouse-address/logo', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    }).then(r => r.data);
  },
  clearLogo: () => api.delete('/warehouse-address/logo').then(r => r.data),
  // Absolute URL for the raw PNG preview. Attach `?t=...` to bust the
  // browser cache when the SA re-uploads a new logo mid-session.
  logoUrl: (bust) => `${API_BASE}/warehouse-address/logo${bust ? `?t=${bust}` : ''}`,
};

export const brandingApi = {
  status: () => api.get('/branding/status').then(r => r.data),
  uploadBanner: (file, onProgress) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/branding/login-banner', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    }).then(r => r.data);
  },
  clearBanner: () => api.delete('/branding/login-banner').then(r => r.data),
  // iter45 — one-off delete for a specific banner in the rotation gallery.
  removeBanner: (bannerId) => api.delete(`/branding/login-banner/${encodeURIComponent(bannerId)}`).then(r => r.data),
  // iter46 — Banner Scheduler: pin/unpin a banner to a date window (IST,
  // inclusive). Body: { start: 'YYYY-MM-DD', end: 'YYYY-MM-DD' }.
  setSchedule: (bannerId, start, end) =>
    api.put(`/branding/login-banner/${encodeURIComponent(bannerId)}/schedule`, { start, end }).then(r => r.data),
  clearSchedule: (bannerId) =>
    api.delete(`/branding/login-banner/${encodeURIComponent(bannerId)}/schedule`).then(r => r.data),
  // iter45 — pass an `index` to fetch a specific banner from the gallery.
  bannerUrl: (bust, index) => {
    const parts = [];
    if (typeof index === 'number' && !isNaN(index)) parts.push(`index=${index}`);
    if (bust) parts.push(`t=${bust}`);
    const qs = parts.length ? `?${parts.join('&')}` : '';
    return `${API_BASE}/branding/login-banner${qs}`;
  },
};

export const brandsApi = {
  list: (opts = {}) => api.get('/brands', { params: opts }).then(r => r.data),
  create: (body) => api.post('/brands', body).then(r => r.data),
  update: (name, body) => api.put(`/brands/${encodeURIComponent(name)}`, body).then(r => r.data),
  remove: (name) => api.delete(`/brands/${encodeURIComponent(name)}`).then(r => r.data),
};

export const docsApi = {
  regenerate: () => api.post('/docs/regenerate').then(r => r.data),
};

// Helper to trigger a file download that requires Authorization header
export async function downloadAuthed(url, filename) {
  const token = localStorage.getItem('fm_token');
  const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a); a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
}

// Open an authed URL (e.g. PDF waybill) in a new tab via a blob URL so we
// never leak the JWT in the querystring. Blob URL is revoked when the tab
// unloads — same-origin only.
export async function openAuthedInNewTab(url) {
  const token = localStorage.getItem('fm_token');
  const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(body || `Open failed (${res.status})`);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const w = window.open(objectUrl, '_blank', 'noopener,noreferrer');
  if (!w) {
    // Popup blocked — fall back to same-tab navigation.
    window.location.href = objectUrl;
  }
  // Give the browser a beat to hand the URL off before we revoke.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

// POST variant of `openAuthedInNewTab` — used by batch-print. Returns the
// parsed response headers dictionary so callers can react to skipped ids
// (X-Waybill-Skipped) without another round-trip.
export async function openAuthedInNewTabPost(url, body) {
  const token = localStorage.getItem('fm_token');
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.json())?.detail; } catch { /* not json */ }
    throw new Error(detail || `Open failed (${res.status})`);
  }
  const included = res.headers.get('x-waybill-included');
  const skippedRaw = res.headers.get('x-waybill-skipped') || '';
  const skipped = skippedRaw ? skippedRaw.split(',').filter(Boolean) : [];
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const w = window.open(objectUrl, '_blank', 'noopener,noreferrer');
  if (!w) window.location.href = objectUrl;
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  return { included: included ? Number(included) : null, skipped };
}

export default api;
